<template>
  <div>
    <tabs-modal
      v-model="isModalOpen"
      :initial-tab="initialTab"
      :tabs="[
        {
          id: 'details',
          label: 'Detalle',
          title: 'Detalle del Evento',
        },
        {
          id: 'history',
          label: 'Historial',
          title: 'Historial del Evento',
          subtitle:
            !canUpdateEvent(event) && canUpdateEventExecution(event)
              ? 'Revisa y confirma tus actividades. Una vez confirmadas, el evento se cerrará y no podrás modificarlas'
              : '',
        },
      ]"
      @close="onCloseModal"
    >
      <template v-if="event" #details>
        <event-details
          :event="event"
          :customer-shipping-addresses="customerShippingAddresses"
          :disabled="!canUpdateEvent(event)"
          :read-only="isReadOnly"
          @update="onUpdate"
        />
      </template>

      <template v-if="event" #history>
        <event-history
          ref="eventHistory"
          :event="event"
          :pending-execution="canUpdateEvent(event)"
          :execution-disabled="!canUpdateEventExecution(event)"
          :read-only="isReadOnly"
        />
      </template>
      <template
        v-if="!isReadOnly && event && canUpdateEventExecution(event)"
        #actions-history
      >
        <v-btn
          color="#E20809"
          dark
          :elevation="0"
          :loading="isLoading"
          @click="onUpdateExecution"
        >
          Confirmar Actividades
        </v-btn>
      </template>
    </tabs-modal>

    <cool-light-box
      :items="galleryImages"
      :index="galleryImagesIndex"
      @close="onCloseImageGallery"
    />
  </div>
</template>

<script>
  import TabsModal from '@/components/modals/TabsModal.vue';
  import EventDetails from './EventDetails.vue';
  import EventHistory from './EventHistory.vue';

  import { getCustomerShippingAddresses } from '@/repositories/Customer';
  import { EventAction, EventKind } from '@/core/constants/schedule/event.js';
  import { matchEvent } from '@/utilities/schedule/event.js';
  import useGeolocation from '@/mixins/useGeolocation.js';
  import { eventBus } from '@/main';

  import CoolLightBox from 'vue-cool-lightbox';
  import { mapGetters, mapActions } from 'vuex';
  import moment from 'moment';

  export default {
    name: 'EventModal',
    components: {
      TabsModal,
      EventDetails,
      EventHistory,
      CoolLightBox,
    },
    mixins: [useGeolocation],
    data: () => {
      return {
        targetEvent: null,
        customerShippingAddresses: [],
        initialTab: null,
        isLoading: false,
        isModalOpen: false,
        isImageGalleryOpen: false,
        galleryImages: [],
        galleryImagesIndex: null,
      };
    },
    computed: {
      ...mapGetters('auth', ['user']),
      ...mapGetters('schedule', [
        'events',
        'canUpdateEvent',
        'canUpdateEventExecution',
      ]),
      ...mapGetters('scheduleNotification', [
        'todayEvents',
        'customersFromDescendants',
      ]),
      isReadOnly() {
        if (!this.event) return true;
        return this.user?.id !== this.event.creator?.user?.id;
      },
      event() {
        if (!this.targetEvent) return null;
        return (
          this.events.find((e) => matchEvent(e, this.targetEvent)) ??
          this.todayEvents.find((e) => matchEvent(e, this.targetEvent)) ??
          this.targetEvent
        );
      },
    },
    watch: {
      event: {
        immediate: true,
        handler(newEvent, oldEvent) {
          const newCode = newEvent?.customer?.code;
          const oldCode = oldEvent?.customer?.code;

          if (!newCode || this.readOnly || this.disabled) return;
          if (newCode === oldCode) return;

          this.loadCustomerShippingAddresses(newCode);
        },
      },
    },
    created() {
      this.getEventTypes();

      eventBus.$on('scheduleOpenModalEvent', (payload) => {
        this.targetEvent = payload?.event;
        this.initialTab = payload?.tab ?? 'details';
        this.isModalOpen = true;
      });

      eventBus.$on('scheduleOpenImageGallery', (payload) => {
        this.galleryImages = payload?.images || [];
        this.galleryImagesIndex = payload?.index ?? 0;
      });
    },
    beforeDestroy() {
      eventBus.$off('scheduleOpenModalEvent');
    },
    methods: {
      ...mapActions('schedule', ['getEventTypes', 'updateEventExecution']),
      onCloseModal() {
        this.eventId = null;
        this.initialTab = 'details';
      },
      onCloseImageGallery() {
        this.galleryImages = [];
        this.galleryImagesIndex = null;
      },
      onUpdate(updatedEvent) {
        const becameRecurrent =
          this.targetEvent?.kind === EventKind.SINGLE &&
          updatedEvent.kind === EventKind.TEMPLATE;

        if (!becameRecurrent) return;

        const originalDate = this.targetEvent.startDate;
        const occ = (e) =>
          e.id === updatedEvent.id && moment(e.startDate).isSame(originalDate);

        this.targetEvent =
          this.events.find(occ) ?? this.todayEvents.find(occ) ?? updatedEvent;
      },
      async loadCustomerShippingAddresses(customerCode) {
        this.isLoadingCustomerLocations = true;
        this.customerShippingAddresses = [];

        try {
          const channel = this.user?.VTWEG;
          const salesOrganization = this.user?.VKORG;

          const data = await getCustomerShippingAddresses(customerCode, {
            salesOrganization,
            channel,
          });

          this.customerShippingAddresses = data;
        } catch (error) {
          this.$toast.error('Error al cargar las ubicaciones del cliente');
        } finally {
          this.isLoadingCustomerLocations = false;
        }
      },
      async onUpdateExecution() {
        if (this.isReadOnly) return;
        this.isLoading = true;

        const geolocation = await this.getGeolocationPosition();
        const subactivities =
          this.$refs.eventHistory?.executionActivities || [];

        const { error } = await this.updateEventExecution({
          eventId: this.event?.id,
          execution: {
            subactivityKeys: subactivities,
          },
          action: {
            userId: this.user?.id,
            type: EventAction.CONFIRM_ACTIVITIES,
            geolocation,
          },
        });

        if (error) {
          this.isLoading = false;
          this.$toast.error(error);
          return;
        }

        this.$toast.success('Evento actualizado con éxito');
        this.isLoading = false;
      },
    },
  };
</script>

<style scoped></style>
